import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import {
  Botao,
  FormRef,
  FormUnform,
  ModalBase,
  MoedaInputUnform,
  SelectOpcao,
  SelectUnform
} from 'src/componentes'
import { Api } from 'src/servicos'
import {
  ProtocoloRescisaoDetalheDto,
  AtualizarDadosFinanceirosRescisao as AtualizarDadosFinanceirosRescisaoDados
} from 'src/servicos/api/rescisao'
import { SituacaoParcela, SituacaoParcelaFiltro } from 'src/tipos'

import { AcoesModal, CampoReadOnly, CorpoModal, TituloModal } from './styles'

const opcoesTaxaRescisao: SelectOpcao[] = [
  { id: '0', texto: '0%' },
  { id: '10', texto: '10%' },
  { id: '15', texto: '15%' },
  { id: '25', texto: '25%' }
]

const opcoesSituacaoParcela: SelectOpcao[] = Object.keys(SituacaoParcelaFiltro).map(
  chave => ({
    id: chave,
    texto: SituacaoParcela[chave as keyof typeof SituacaoParcela]
  })
)

export interface ModalEditarFinanceiroRescisaoRef {
  abrir: (protocolo: ProtocoloRescisaoDetalheDto) => void
  fechar: () => void
}

interface ModalEditarFinanceiroRescisaoProps {
  backdrop?: boolean
  aoSalvar: () => void
}

const ModalEditarFinanceiroRescisaoComponente: React.ForwardRefRenderFunction<
  ModalEditarFinanceiroRescisaoRef,
  ModalEditarFinanceiroRescisaoProps
> = ({ backdrop, aoSalvar }, ref) => {
  const formRef = useRef<FormRef>(null)
  const [aberta, definirAberta] = useState(false)
  const [carregando, definirCarregando] = useState(false)
  const [protocoloId, definirProtocoloId] = useState<string>(null)
  const [protocolo, definirProtocolo] = useState<ProtocoloRescisaoDetalheDto>(
    null
  )
  const [dadosIniciais, definirDadosIniciais] = useState<{
    statusParcelaRescisao?: string
    percentualTaxaRescisao?: string
    valorFinalRescisao?: number
  }>(null)
  // O react-select (via SelectUnform) mantém estado interno próprio que não é resetado só por
  // `initialData` mudar — sem essa key, reabrir o modal para outro protocolo (ou o mesmo, de novo)
  // podia reter a seleção anterior de sessões passadas do formulário.
  const [chaveFormulario, definirChaveFormulario] = useState(0)

  const fechar = () => {
    definirAberta(false)
    definirProtocoloId(null)
    definirProtocolo(null)
    definirDadosIniciais(null)
    definirCarregando(false)
  }

  const abrir = (protocolo: ProtocoloRescisaoDetalheDto) => {
    definirProtocoloId(protocolo.id)
    definirProtocolo(protocolo)
    definirDadosIniciais({
      statusParcelaRescisao: protocolo.statusParcelaRescisao,
      percentualTaxaRescisao:
        protocolo.percentualTaxaRescisao !== null &&
        protocolo.percentualTaxaRescisao !== undefined
          ? String(protocolo.percentualTaxaRescisao)
          : undefined,
      valorFinalRescisao: protocolo.totalAPagar ?? undefined
    })
    definirChaveFormulario(chave => chave + 1)
    definirAberta(true)
  }

  useImperativeHandle(ref, () => ({ abrir, fechar }))

  const salvar = async (dados: {
    statusParcelaRescisao?: string
    percentualTaxaRescisao?: string
    valorFinalRescisao?: number
  }) => {
    if (!protocoloId) return

    // Valor final digitado manualmente tem prioridade sobre o % (não faz sentido enviar os dois —
    // o backend rejeita). Só considera "alterado" se for diferente do total atual do protocolo.
    const valorFinalAlterado =
      protocolo?.casoEspecial &&
      dados.valorFinalRescisao !== undefined &&
      dados.valorFinalRescisao !== null &&
      dados.valorFinalRescisao !== protocolo?.totalAPagar

    const payload: AtualizarDadosFinanceirosRescisaoDados = {
      statusParcelaRescisao: dados.statusParcelaRescisao,
      percentualTaxaRescisao:
        protocolo?.casoEspecial && !valorFinalAlterado
          ? Number(dados.percentualTaxaRescisao)
          : undefined,
      valorFinalRescisao: valorFinalAlterado
        ? dados.valorFinalRescisao
        : undefined
    }

    try {
      definirCarregando(true)
      await Api.AtualizarDadosFinanceirosRescisao(protocoloId, payload)
      aoSalvar()
      fechar()
    } finally {
      definirCarregando(false)
    }
  }

  return (
    <ModalBase
      id="modal-editar-financeiro-rescisao"
      aberta={aberta}
      backdrop={backdrop}
    >
      <CorpoModal>
        <TituloModal>Editar dados financeiros do protocolo</TituloModal>
        <CampoReadOnly>
          <span className="rotulo">Parcelamento do curso</span>
          <span className="valor">
            {protocolo?.parcelamento ? `${protocolo.parcelamento}x` : '—'}
          </span>
        </CampoReadOnly>
        <CampoReadOnly>
          <span className="rotulo">Forma de pagamento do curso</span>
          <span className="valor">{protocolo?.formaPagamentoCurso ?? '—'}</span>
        </CampoReadOnly>
        {!protocolo?.casoEspecial && (
          <CampoReadOnly>
            <span className="rotulo">% Taxa de rescisão</span>
            <span className="valor">15% (fixo)</span>
          </CampoReadOnly>
        )}
        {dadosIniciais && (
          <FormUnform
            key={chaveFormulario}
            ref={formRef}
            dadosIniciais={dadosIniciais}
            acaoSucesso={salvar}
          >
            {protocolo?.casoEspecial && (
              <SelectUnform
                id="slct_taxa_rescisao_financeiro"
                name="percentualTaxaRescisao"
                label="% Taxa de rescisão (caso especial)"
                opcoes={opcoesTaxaRescisao}
              />
            )}
            {protocolo?.casoEspecial && (
              <MoedaInputUnform
                id="ipt_valor_final_rescisao_financeiro"
                name="valorFinalRescisao"
                label="Valor final da rescisão (caso especial)"
              />
            )}
            <SelectUnform
              id="slct_status_parcela_rescisao_financeiro"
              name="statusParcelaRescisao"
              label="Status da parcela de rescisão"
              opcoes={opcoesSituacaoParcela}
            />
            <AcoesModal>
              <Botao
                texto="Cancelar"
                tema="Secundario"
                type="button"
                disabled={carregando}
                onClick={fechar}
              />
              <Botao texto="Salvar" type="submit" carregando={carregando} />
            </AcoesModal>
          </FormUnform>
        )}
      </CorpoModal>
    </ModalBase>
  )
}

export const ModalEditarFinanceiroRescisao = forwardRef(
  ModalEditarFinanceiroRescisaoComponente
)
